import type { ReactNode } from 'react';

import { Button } from './Button';
import { Icons } from './icons';
import { MenuItem } from './MenuItem';
import { MenuList, MenuRow } from './MenuList';
import { Popover } from './Popover';
import styles from './SplitButton.module.css';

export interface SplitItem {
  label: string;
  icon: ReactNode;
  onSelect: () => void;
}

interface Props {
  /** The main action's label and handler. */
  label: string;
  icon?: ReactNode;
  onMain: () => void;
  /** Accessible name of the chevron that opens the menu of alternatives. */
  menuLabel: string;
  items: readonly SplitItem[];
  /** `primary` is the page's main action; `quiet` is a secondary one beside it. */
  tone?: 'primary' | 'quiet';
  title?: string;
}

/** A main action plus a chevron menu of related ones (quick / advanced creation). */
export function SplitButton({
  label,
  icon,
  onMain,
  menuLabel,
  items,
  tone = 'primary',
  title,
}: Props) {
  return (
    <div className={`${styles.split} ${tone === 'quiet' ? styles.quiet : ''}`}>
      <Button
        size="sm"
        variant={tone === 'primary' ? 'primary' : 'ghost'}
        className={styles.main}
        title={title ?? label}
        onClick={onMain}
      >
        {icon} {label}
      </Button>
      <Popover
        label={menuLabel}
        width={260}
        alignEnd
        className={styles.moreRoot}
        trigger={(props) => (
          <button
            type="button"
            {...props}
            className={styles.more}
            aria-label={menuLabel}
            title={menuLabel}
          >
            <Icons.chevronDown />
          </button>
        )}
      >
        {(close) => (
          <MenuList>
            {items.map((item) => (
              <MenuRow key={item.label}>
                <MenuItem
                  icon={item.icon}
                  onClick={() => {
                    close();
                    item.onSelect();
                  }}
                >
                  {item.label}
                </MenuItem>
              </MenuRow>
            ))}
          </MenuList>
        )}
      </Popover>
    </div>
  );
}
